"use client"

import { useState, useEffect } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card"
import { Button } from "../../ui/button"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../ui/table"
import { Textarea } from "../../ui/textarea"
import { useToast } from "../../hooks/use-toast"
import { apiClient } from "../../lib/apiClient"
import { Settings, Save, History } from "lucide-react"

interface Configuration {
  id: number
  name: string
  description: string | null
  value: string | null
}

interface UpdateConfigurationRequest {
  Id: number
  Value: string
}

export function ConfigSettingsPage() {
  const [configurations, setConfigurations] = useState<Configuration[]>([])
  const [loading, setLoading] = useState(true)
  const [updatingId, setUpdatingId] = useState<number | null>(null)
  const [editedValues, setEditedValues] = useState<Record<number, string>>({})
  const { toast } = useToast()

  // Fetch all configurations
  const fetchConfigurations = async () => {
    try {
      setLoading(true)
      const response = await apiClient.get("/admin/config-settings")
      const data = Array.isArray(response.data) ? response.data : []
      
      setConfigurations(data)
      
      // Initialize edited values with current values
      const initialValues: Record<number, string> = {}
      data.forEach((config: Configuration) => {
        initialValues[config.id] = config.value || ""
      })
      setEditedValues(initialValues)
    } catch (error: any) {
      console.error("Error fetching configurations:", error)
      toast({
        title: "Error",
        description: error?.response?.data?.message || error?.message || "Failed to load configuration settings",
        duration: 4000,
        className: "border border-red-300",
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchConfigurations()
  }, [])

  // Handle value change
  const handleValueChange = (id: number, value: string) => {
    setEditedValues((prev) => ({
      ...prev,
      [id]: value,
    }))
  }

  // Update configuration
  const handleUpdate = async (config: Configuration) => {
    const newValue = editedValues[config.id] ?? config.value ?? ""
    
    if (newValue === (config.value || "")) {
      toast({
        title: "No Changes",
        description: "No changes detected for this configuration.",
        duration: 3000,
      })
      return
    }

    try {
      setUpdatingId(config.id)
      const payload: UpdateConfigurationRequest = {
        Id: config.id,
        Value: newValue,
      }

      await apiClient.post("/admin/config-settings", payload)
      
      toast({
        title: "Success",
        description: "Configuration updated successfully!",
        duration: 3000,
        className: "border border-green-300",
      })

      // Refresh configurations
      await fetchConfigurations()
    } catch (error: any) {
      console.error("Error updating configuration:", error)
      toast({
        title: "Error",
        description: error?.response?.data?.message || error?.message || "Failed to update configuration",
        duration: 4000,
        className: "border border-red-300",
      })
    } finally {
      setUpdatingId(null)
    }
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <Card className="w-full bg-white border border-gray-200 rounded-lg shadow-sm">
          <CardContent className="p-8 text-center text-gray-500">
            <p>Loading configuration settings...</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <Card className="w-full bg-white border border-gray-200 rounded-lg shadow-sm">
        <CardHeader className="border-b border-gray-200 bg-white p-6">
          <CardTitle className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
            <Settings className="h-6 w-6 text-blue-600" />
            Configuration Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0 w-full">
          {configurations.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <p>No configuration settings found.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50 border-b border-gray-200">
                    <TableHead className="font-semibold text-gray-700 p-4">Id</TableHead>
                    <TableHead className="font-semibold text-gray-700 p-4">Name</TableHead>
                    <TableHead className="font-semibold text-gray-700 p-4">Description</TableHead>
                    <TableHead className="font-semibold text-gray-700 p-4">Value</TableHead>
                    <TableHead className="font-semibold text-gray-700 p-4 w-32">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {configurations.map((config) => (
                    <TableRow key={config.id} className="hover:bg-gray-50 border-b border-gray-100">
                      <TableCell className="p-4 text-gray-900">{config.id}</TableCell>
                      <TableCell className="p-4 font-medium text-gray-900">{config.name}</TableCell>
                      <TableCell className="p-4 text-gray-700">
                        {config.description || "N/A"}
                      </TableCell>
                      <TableCell className="p-4">
                        <Textarea
                          rows={2}
                          className="w-full min-w-[400px] resize-none"
                          value={editedValues[config.id] ?? config.value ?? ""}
                          onChange={(e) => handleValueChange(config.id, e.target.value)}
                          placeholder="Enter configuration value"
                        />
                      </TableCell>
                      <TableCell className="p-4">
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => handleUpdate(config)}
                            disabled={updatingId === config.id}
                            className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-1 font-medium"
                          >
                            <Save className="h-4 w-4" />
                            {updatingId === config.id ? "Updating..." : "Update"}
                          </Button>
                          {/* History button - can be implemented later */}
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex items-center gap-1"
                            title="View History"
                            disabled
                          >
                            <History className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

